// Welle 4.1 — Worker health: clear headline summary
// ("15 Worker: 12 aktiv, 2 Warnungen, 1 Fehler"), clickable workers
// with last error + runtime details.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity, ChevronDown, ChevronUp } from "lucide-react";
import { SectionCard, Pill, StatusDot, formatDateTime } from "./AdminV2Kit";

function jobLevel(j) {
  if (j.last_status === "failed" || j.last_status === "error") return "critical";
  if (!j.last_status) return "attention";
  return "healthy";
}

function JobDetails({ job, t }) {
  return (
    <div
      data-testid={`adminv2-worker-details-${job.job_id}`}
      className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-lg bg-zinc-900/60 px-3 py-2.5 text-[11px] text-zinc-400 sm:grid-cols-4"
    >
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-600">
          {t("modules.workers.lastRun")}
        </div>
        {formatDateTime(job.last_run_at) || t("modules.workers.never")}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-600">
          {t("modules.workers.duration")}
        </div>
        {job.last_duration_ms != null
          ? `${Math.round(job.last_duration_ms / 100) / 10}s`
          : "—"}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-600">
          {t("modules.workers.nextRun")}
        </div>
        {formatDateTime(job.next_run_at) || "—"}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-600">
          {t("modules.workers.statusLabel")}
        </div>
        {job.last_status || t("modules.workers.never")}
      </div>
      {job.last_error && (
        <div className="col-span-2 sm:col-span-4">
          <div className="text-[10px] uppercase tracking-widest text-rose-400/70">
            {t("modules.workers.lastError")}
          </div>
          <div className="break-words font-mono text-[11px] text-rose-300">
            {job.last_error}
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkersCard({ data, developer = false }) {
  const { t } = useTranslation("admin");
  const [openId, setOpenId] = useState(null);
  if (!data?.ok) return null;
  const jobs = data.jobs || [];
  const failed = jobs.filter((j) => jobLevel(j) === "critical").length;
  const warn = jobs.filter((j) => jobLevel(j) === "attention").length;
  const active = jobs.length - failed - warn;
  const status = failed > 0 ? "critical" : warn > 0 ? "attention" : "healthy";
  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));

  return (
    <SectionCard
      title={t("modules.workers.title")}
      icon={Activity}
      status={status}
      testid="adminv2-workers-card"
      actions={
        <div className="flex items-center gap-1.5">
          {data.leader === true && <Pill tone="zinc">{t("modules.workers.leaderActive")}</Pill>}
          {data.running === false && (
            <Pill tone="amber">{t("modules.workers.schedulerOff")}</Pill>
          )}
        </div>
      }
    >
      {/* Headline summary */}
      <div
        data-testid="adminv2-workers-summary"
        className="flex flex-wrap items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-950/50 px-3.5 py-2.5 text-sm text-zinc-200"
      >
        <span className="font-medium">
          {t("modules.workers.summaryTotal", { count: jobs.length })}
        </span>
        <Pill tone="emerald">{t("modules.workers.summaryActive", { count: active })}</Pill>
        <Pill tone={warn > 0 ? "amber" : "zinc"}>
          {t("modules.workers.summaryWarn", { count: warn })}
        </Pill>
        <Pill tone={failed > 0 ? "rose" : "zinc"}>
          {t("modules.workers.summaryFailed", { count: failed })}
        </Pill>
      </div>

      {/* Clickable worker list */}
      <ul className="space-y-1">
        {jobs.map((j) => {
          const open = openId === j.job_id;
          return (
            <li key={j.job_id}>
              <button
                type="button"
                data-testid={`adminv2-worker-row-${j.job_id}`}
                onClick={() => toggle(j.job_id)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-950/50 px-3.5 py-2 text-left transition-colors hover:border-zinc-700"
              >
                <span className="flex min-w-0 items-center gap-2 text-xs text-zinc-300">
                  <StatusDot level={jobLevel(j)} className="h-1.5 w-1.5 shrink-0" />
                  <span className={developer ? "truncate font-mono text-[11px]" : "truncate"}>
                    {developer ? j.job_id : j.job_id.replace(/_/g, " ")}
                  </span>
                  {j.last_error && (
                    <Pill tone="rose">{t("modules.workers.hasError")}</Pill>
                  )}
                </span>
                <span className="flex shrink-0 items-center gap-2 text-[11px] text-zinc-500">
                  {j.last_duration_ms != null && (
                    <span className="tabular-nums">
                      {Math.round(j.last_duration_ms / 100) / 10}s
                    </span>
                  )}
                  {formatDateTime(j.last_run_at) || t("modules.workers.never")}
                  {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </span>
              </button>
              {open && <JobDetails job={j} t={t} />}
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}
